import { useRef, useState } from "react";
import { useRedirect } from "react-admin";
import loginAvatar from "./../static/login-avatar.png";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField } from "mui-rff";
import { Form } from "react-final-form";
import NotificationMessage, {
  notificationType,
} from "../components/NotificationMessage/NotificationMessage";
import fdAuthProvider from "../authProvider/authProvider";
import { routePaths } from "./SidebarMenu";
import { useLocation } from "react-router-dom";

const classes = {
  wrapper: {
    background: "linear-gradient(180deg, #7BB325 0%, #1A7C65 100%)",
    paddingTop: "7em",
    height: "100vh",
  },
  avatar: {
    width: "46px",
    height: "46px",
    position: "absolute",
    top: "-23px",
    left: "calc(50% - 23px)",
    zIndex: 1,
  },
  card: {
    width: "310px",
    marginLeft: "auto",
    marginRight: "auto",
    position: "relative",
    overflow: "visible",
    textAlign: "center",
    padding: "40px 30px",
  },
} as const;

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

interface FormProps {
  initialValues: FormData;
  onLoading: Function;
  onError: Function;
}

const PasswordChangeForm = (props: FormProps) => {
  const { initialValues, onLoading, onError } = props;
  const location = useLocation().search;
  const token = new URLSearchParams(location).get("token");
  const redirect = useRedirect();

  async function onSubmit(values: FormData) {
    try {
      onLoading(true);

      await fdAuthProvider.changePassword({
        password: values.newPassword,
        token: token as string,
      });

      redirect(`/${routePaths.LOGIN}`);
    } catch (error: any) {
      onError("An error occured");
      console.log(error);
    }
  }

  async function validate(values: FormData) {
    const regex = /\d/; // regex to check for number
    const spChars = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/; // regex to check for special character

    if (!values.newPassword) {
      return { newPassword: "Required" };
    }

    if (
      values.newPassword.length < 9 ||
      !regex.test(values.newPassword) ||
      !spChars.test(values.newPassword)
    ) {
      return {
        newPassword:
          "Needs to be at least 9 characters long, and include 1 number and 1 special character",
      };
    }

    if (!values.confirmPassword) {
      return { confirmPassword: "Required" };
    }

    if (values.newPassword !== values.confirmPassword) {
      return { confirmPassword: "Please enter the same password as above" };
    }

    return;
  }

  if (!token) {
    return <></>;
  }

  return (
    <>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Box mt={2} mb={4}>
              <TextField
                name="newPassword"
                label="New password"
                placeholder="Enter password"
                InputLabelProps={{
                  shrink: true,
                }}
                type="password"
                InputProps={{ disableUnderline: true }}
              />
            </Box>
            <Box mt={2} mb={4}>
              <TextField
                name="confirmPassword"
                label="Confirm password"
                placeholder="Enter password"
                InputLabelProps={{
                  shrink: true,
                }}
                type="password"
                InputProps={{ disableUnderline: true }}
              />
            </Box>
            <Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                fullWidth
              >
                Change password
              </Button>
            </Box>
          </form>
        )}
      />
    </>
  );
};

const PasswordChange = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const notificationRef = useRef<any>();

  return (
    <>
        <Box sx={classes.wrapper}>
          <Card sx={classes.card}>
            <Avatar src={loginAvatar} sx={classes.avatar} />
            <Typography variant="h2" component="h2">
              Change password
            </Typography>

            {loading && (
              <Box mt={2}>
                <CircularProgress />
              </Box>
            )}

            {!loading && (
              <PasswordChangeForm
                initialValues={{ newPassword: "", confirmPassword: "" }}
                onLoading={(isLoading: boolean) => {
                  setLoading(isLoading);
                }}
                onError={(error: string) =>
                  notificationRef.current?.show(error, notificationType.Error)
                }
              />
            )}
          </Card>
        </Box>
        <NotificationMessage ref={notificationRef} />
  </>);
};

export default PasswordChange;
